/**
 * Safe property access utilities for object path traversal
 * Used primarily for form data access and theme token resolution
 */

// Type definitions
export interface PathGetOptions {
  separator?: string;
  arrayIndexRegex?: RegExp;
}

export interface FlattenedObject {
  [path: string]: any;
}

/**
 * Safely get a value from an object using a dot-separated path
 * @param obj - Object to get value from
 * @param path - Dot-separated path (e.g. 'user.profile.name')
 * @param defaultValue - Default value to return if path not found
 * @returns Value at path or default value
 */
export const pathGet = (obj: any, path: string, defaultValue: any = undefined): any => {
  // Handle invalid inputs
  if (!obj || typeof obj !== 'object' || !path || typeof path !== 'string') {
    return defaultValue;
  }
  
  // Handle empty path
  if (path === '') {
    return obj;
  }
  
  try {
    // IMPORTANT: First check if the path exists as a literal key in the object.
    // This handles form data that uses flat dot-notation keys (e.g., "gas.gasSupplier")
    // where the entire path is a single key rather than nested objects.
    if (Object.prototype.hasOwnProperty.call(obj, path)) {
      return obj[path];
    }
    
    const keys = parsePath(path);
    let current = obj;
    
    for (const key of keys) {
      if (current === null || current === undefined) {
        return defaultValue;
      }
      
      // Handle array indices
      if (Array.isArray(current) && /^\d+$/.test(key)) {
        const index = parseInt(key, 10);
        if (index >= current.length) {
          return defaultValue;
        }
        current = current[index];
      } else if (typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return defaultValue;
      }
    }
    
    return current;
  } catch (error) {
    console.warn('Path get error:', error);
    return defaultValue;
  }
};

/**
 * Safely set a value in an object using a dot-separated path
 * @param obj - Object to set value in (will be mutated)
 * @param path - Dot-separated path
 * @param value - Value to set
 * @returns The original object (mutated)
 */
export const pathSet = (obj: any, path: string, value: any): any => {
  if (!obj || typeof obj !== 'object' || !path || typeof path !== 'string') {
    return obj;
  }
  
  try {
    const keys = parsePath(path);
    let current = obj;
    
    // Navigate to the parent of the target property
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      const nextKey = keys[i + 1];
      
      if (!key) continue;
      
      // Determine if next key is an array index
      const isNextKeyArrayIndex = nextKey ? /^\d+$/.test(nextKey) : false;
      
      if (!(key in current) || current[key] === null || typeof current[key] !== 'object') {
        // Create new object or array based on next key
        current[key] = isNextKeyArrayIndex ? [] : {};
      }
      
      current = current[key];
    }
    
    // Set the final value
    const finalKey = keys[keys.length - 1];
    if (finalKey) {
      current[finalKey] = value;
    }
    
    return obj;
  } catch (error) {
    console.warn('Path set error:', error);
    return obj;
  }
};

/**
 * Create a new object with a value set at the specified path (immutable)
 * @param obj - Original object
 * @param path - Dot-separated path
 * @param value - Value to set
 * @returns New object with value set
 */
export const pathSetImmutable = (obj: any, path: string, value: any): any => {
  if (!obj || typeof obj !== 'object' || !path || typeof path !== 'string') {
    return obj;
  }
  
  try {
    const keys = parsePath(path);
    
    // Deep clone the object
    const result = Array.isArray(obj) ? [...obj] : { ...obj };
    let current = result;
    
    // Navigate and create path
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      const nextKey = keys[i + 1];
      
      if (!key) continue;
      
      const isNextKeyArrayIndex = nextKey ? /^\d+$/.test(nextKey) : false;
      
      if (Array.isArray(current)) {
        const index = parseInt(key, 10);
        if (!current[index] || typeof current[index] !== 'object') {
          current[index] = isNextKeyArrayIndex ? [] : {};
        } else {
          current[index] = Array.isArray(current[index]) ? [...current[index]] : { ...current[index] };
        }
        current = current[index];
      } else {
        if (!current[key] || typeof current[key] !== 'object') {
          current[key] = isNextKeyArrayIndex ? [] : {};
        } else {
          current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...current[key] };
        }
        current = current[key];
      }
    }
    
    // Set final value
    const finalKey = keys[keys.length - 1];
    if (finalKey) {
      current[finalKey] = value;
    }
    
    return result;
  } catch (error) {
    console.warn('Immutable path set error:', error);
    return obj;
  }
};

/**
 * Check if a path exists in an object
 * @param obj - Object to check
 * @param path - Dot-separated path
 * @returns True if path exists
 */
export const pathHas = (obj: any, path: string): boolean => {
  if (!obj || typeof obj !== 'object' || !path || typeof path !== 'string') {
    return false;
  }
  
  try {
    // First check if the path exists as a literal key (flat dot-notation)
    if (Object.prototype.hasOwnProperty.call(obj, path)) {
      return true;
    }
    
    const keys = parsePath(path);
    let current = obj;
    
    for (const key of keys) {
      if (current === null || current === undefined || typeof current !== 'object') {
        return false;
      }
      
      if (Array.isArray(current)) {
        const index = parseInt(key, 10);
        if (isNaN(index) || index >= current.length || index < 0) {
          return false;
        }
        current = current[index];
      } else if (!(key in current)) {
        return false;
      } else {
        current = current[key];
      }
    }
    
    return true;
  } catch (_error) {
    return false;
  }
};

/**
 * Delete a property at the specified path
 * @param {Object} obj - Object to delete from (will be mutated)
 * @param {string} path - Dot-separated path
 * @returns {boolean} True if property was deleted
 */
export const pathDelete = (obj: any, path: string): boolean => {
  if (!obj || typeof obj !== 'object' || !path || typeof path !== 'string') {
    return false;
  }
  
  try {
    const keys = parsePath(path);
    let current = obj;
    
    // Navigate to parent
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      
      if (!key || current === null || current === undefined || typeof current !== 'object') {
        return false;
      }
      
      if (Array.isArray(current)) {
        const index = parseInt(key, 10);
        if (isNaN(index) || index >= current.length || index < 0) {
          return false;
        }
        current = current[index];
      } else if (!(key in current)) {
        return false;
      } else {
        current = current[key];
      }
    }
    
    // Delete final property
    const finalKey = keys[keys.length - 1];
    
    if (!finalKey) {
      return false;
    }
    
    if (Array.isArray(current)) {
      const index = parseInt(finalKey, 10);
      if (!isNaN(index) && index >= 0 && index < current.length) {
        current.splice(index, 1);
        return true;
      }
    } else if (current && typeof current === 'object' && finalKey in current) {
      delete current[finalKey];
      return true;
    }
    
    return false;
  } catch (error) {
    console.warn('Path delete error:', error);
    return false;
  }
};

/**
 * Get all paths and values in an object as flat key-value pairs
 * @param obj - Object to flatten
 * @param prefix - Path prefix
 * @returns Flattened object
 */
export const flattenPaths = (obj: any, prefix: string = ''): FlattenedObject => {
  const flattened = {};
  
  if (!obj || typeof obj !== 'object') {
    return { [prefix]: obj };
  }
  
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const path = prefix ? `${prefix}.${index}` : index.toString();
      Object.assign(flattened, flattenPaths(item, path));
    });
  } else {
    Object.keys(obj).forEach(key => {
      const path = prefix ? `${prefix}.${key}` : key;
      Object.assign(flattened, flattenPaths(obj[key], path));
    });
  }
  
  return flattened;
};

/**
 * Unflatten a flat object back to nested structure
 * @param {Object} flatObj - Flat object with dot-separated keys
 * @returns {Object} Nested object
 */
export const unflattenPaths = (flatObj: FlattenedObject): any => {
  const result = {};
  
  Object.keys(flatObj).forEach(path => {
    pathSet(result, path, flatObj[path]);
  });
  
  return result;
};

/**
 * Parse a path string into an array of keys
 * Handles escaped dots and bracket notation
 * @param path - Path to parse
 * @returns Array of path segments
 */
const parsePath = (path: string): string[] => {
  // Handle bracket notation: obj[0].prop -> obj.0.prop
  const normalizedPath = path
    .replace(/\[(\d+)\]/g, '.$1')
    .replace(/\[['"]([^'"]+)['"]\]/g, '.$1');
  
  // Split by dots, but handle escaped dots
  const parts = [];
  let current = '';
  let escaped = false;
  
  for (let i = 0; i < normalizedPath.length; i++) {
    const char = normalizedPath[i];
    
    if (char === '\\' && !escaped) {
      escaped = true;
      continue;
    }
    
    if (char === '.' && !escaped) {
      if (current) {
        parts.push(current);
        current = '';
      }
    } else {
      current += char;
    }
    
    escaped = false;
  }
  
  if (current) {
    parts.push(current);
  }
  
  return parts;
};

/**
 * Escape special characters in a path segment
 * @param {string} segment - Path segment to escape
 * @returns {string} Escaped segment
 */
export const escapePathSegment = (segment: string): string => {
  return segment.replace(/\./g, '\\.');
};

/**
 * Safe path access for form data (with form-specific validation)
 * @param {Object} formData - Form data object
 * @param {string} path - Field path
 * @param {any} defaultValue - Default value
 * @returns {any} Field value or default
 */
export const getFormFieldValue = (formData: any, path: string, defaultValue: any = ''): any => {
  // Handle common form path formats
  const normalizedPath = path.replace(/^form\./, '').replace(/^data\./, '');
  
  return pathGet(formData, normalizedPath, defaultValue);
};

/**
 * Set form field value with validation
 * @param {Object} formData - Form data object
 * @param {string} path - Field path
 * @param {any} value - Value to set
 * @returns {Object} Updated form data
 */
export const setFormFieldValue = (formData: any, path: string, value: any): any => {
  const normalizedPath = path.replace(/^form\./, '').replace(/^data\./, '');
  
  return pathSetImmutable(formData, normalizedPath, value);
};

export default {
  pathGet,
  pathSet,
  pathSetImmutable,
  pathHas,
  pathDelete,
  flattenPaths,
  unflattenPaths,
  escapePathSegment,
  getFormFieldValue,
  setFormFieldValue,
};
